import React from "react";
import { DateTime } from "luxon";
import {
  Constraint,
  Event as E,
  Identifiable,
  list,
  List,
  Milestone,
  OptimizedGantt,
  Project,
  ProjectElementType,
  resource,
  status,
  Task,
} from "@planzone/dto";
import { getDate } from "../elementsOverlay/element/utils/getDate";
import { calculateStartAndEnd } from "../elementsOverlay/element/root/utils";
import { isMilestone } from "../elementsOverlay/element/utils";

type Config = {
  cell: {
    width: number;
    height: number;
  };
};

type ProjectElement = OptimizedGantt[number];

type ListPE = Extract<ProjectElement, { type: ProjectElementType.LIST }>;
type TaskPE = Extract<ProjectElement, { type: ProjectElementType.TASK }>;
type MilestonePE = Extract<
  ProjectElement,
  { type: ProjectElementType.MILESTONE }
>;
type EventPE = Extract<ProjectElement, { type: ProjectElementType.EVENT }>;

function projectElementToList(element: ListPE): Identifiable<List> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startDate: element.startDate,
    endDate: element.endDate,
    createdDate: "",
    parentList: { ...list, uuid: element.parentUuid },
    subLists: [],
    tasks: [],
    events: [],
    milestones: [],
    tags: element.tags,
    attachments: [],
    activityKeyRank: element.referenceKeyRank,
    statusKeyRank: 0,
    attributes: element.attributes,
    minStartDate: element.startDate,
    maxStartDate: "",
    minEndDate: "",
    maxEndDate: element.endDate,
  };
}

function projectElementToTask(element: TaskPE): Identifiable<Task> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startDate: element.startDate,
    endDate: element.endDate,
    createdDate: "",
    completionDate: element.completionDate,
    percentComplete: element.percentComplete,
    allocations: element.allocations,
    status: element.status,
    tags: element.tags,
    attachments: [],
    activityKeyRank: element.referenceKeyRank,
    statusKeyRank: 0,
    attributes: element.attributes,
    duration: 0,
    minStartDate: element.startDate,
    maxEndDate: "",
    lastModified: "",
  };
}

function projectElementToEvent(element: EventPE): Identifiable<E> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startTime: element.startDate,
    endTime: element.endDate,
    createdDate: "",
    organizer: { ...resource, uuid: "" },
    participants: [],
    tags: element.tags,
    attachments: [],
    keyRank: element.referenceKeyRank,
    attributes: element.attributes,
  };
}

function projectElementToMilestone(
  element: MilestonePE,
): Identifiable<Milestone> {
  return {
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    endDate: element.endDate,
    completedAt: element.completionDate,
    createdDate: "",
    list: { ...list, uuid: element.parentUuid },
    state: element.state,
    tags: element.tags,
    attachments: [],
    commentsNbr: 0,
    attachmentsNbr: 0,
    keyRank: element.referenceKeyRank,
    attributes: element.attributes,
    minStartDate: element.endDate,
    maxEndDate: "",
  };
}

function constraintToProjectElement(
  constraint: Constraint & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  },
  pointType: "target" | "origin",
): ProjectElement {
  const getVal = (key: string): string =>
    constraint[`${pointType}${key}` as keyof typeof constraint] as string;
  return {
    uuid: getVal("EntityUuid"),
    parentUuid: "",
    name: "",
    description: "",
    startDate: getVal("StartDate"),
    endDate: getVal("EndDate"),
    completionDate: "",
    percentComplete: 0,
    depth: 0,
    keyRank: 0,
    referenceKeyRank: 0,
    tags: [],
    attributes: {},
    state: "TODO",
    allocations: [],
    parentUuids: [],
    activityRootUuid: "",
    status: { ...status, uuid: "" },
    type:
      getVal("EntityType") === "MILESTONE"
        ? ProjectElementType.MILESTONE
        : getVal("EntityType") === "ACTIVITY"
          ? ProjectElementType.LIST
          : ProjectElementType.TASK,
  };
}

function constraintToEl(
  constraint: Constraint & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  },
  pointType: "target" | "origin",
) {
  const getVal = (key: string): string =>
    constraint[`${pointType}${key}` as keyof typeof constraint] as string;
  const element = constraintToProjectElement(constraint, pointType);

  return getVal("EntityType") === "MILESTONE"
    ? projectElementToMilestone(element as MilestonePE)
    : getVal("EntityType") === "ACTIVITY"
      ? projectElementToList(element as ListPE)
      : projectElementToTask(element as TaskPE);
}

const Context = React.createContext<{
  x: number;
  y: number;
  origin: ProjectElement | null;
  target: ProjectElement | null;
  isDragging: boolean;
  originDateType: Constraint["originDateType"];
  targetDateType: Constraint["originDateType"];
  startDrag(data: {
    x: number;
    y: number;
    element: ProjectElement;
    project: Identifiable<Project>;
    dateType: Constraint["originDateType"];
  }): void;
  dragInElement(data: {
    element: ProjectElement;
    dateType: Constraint["originDateType"];
    snapPoint: { x: number; y: number };
  }): void;
  dragOutElement(data: {
    element: ProjectElement;
    dateType: Constraint["originDateType"];
  }): void;
}>({
  x: 0,
  y: 0,
  isDragging: false,
  origin: null,
  target: null,
  originDateType: "START",
  targetDateType: "START",
  startDrag() {},
  dragInElement() {},
  dragOutElement() {},
});

function useConstraints() {
  return React.useContext(Context);
}

function useDrag(
  move: (x: number, y: number) => void,
  done: () => void,
  deps: any[],
) {
  React.useEffect(() => {
    const cancelSelection = () => done();
    const dispatchChange = (e: MouseEvent | TouchEvent) => {
      const mouseEvent = e as MouseEvent;
      const touchEvent = e as TouchEvent;

      move(
        !!mouseEvent.clientX
          ? mouseEvent.clientX
          : touchEvent.changedTouches?.length > 0
            ? touchEvent.changedTouches[0].clientX
            : 0,
        !!mouseEvent.clientY
          ? mouseEvent.clientY
          : touchEvent.changedTouches?.length > 0
            ? touchEvent.changedTouches[0].clientY
            : 0,
      );
    };

    window.addEventListener("touchmove", dispatchChange);
    window.addEventListener("mousemove", dispatchChange);
    window.addEventListener("mouseup", cancelSelection);
    window.addEventListener("touchend", cancelSelection);

    return () => {
      window.removeEventListener("touchmove", dispatchChange);
      window.removeEventListener("mousemove", dispatchChange);
      window.removeEventListener("mouseup", cancelSelection);
      window.removeEventListener("touchend", cancelSelection);
    };
  }, deps);
}

const HEADER_HEIGHT = 71;
const HEIGHT_RATIO = 8 / 10;
const DAY = 1000 * 60 * 60 * 24;

type Rect = {
  top: number;
  left: number;
  width: number;
  right: number;
  height: number;
};

function getElementRect(
  element: ProjectElement,
  minDate: DateTime,
  cell: Config["cell"],
  userTimezone: string,
  offset: number = 0,
): Rect {
  const completionDate = getDate(element.completionDate, userTimezone);
  const { startDate, endDate } = calculateStartAndEnd(
    element,
    completionDate,
    {
      startDate: getDate(element.startDate, userTimezone),
      endDate: getDate(element.endDate, userTimezone),
    },
    false,
  );
  const durationInDays = (endDate.toMillis() - startDate.toMillis()) / DAY;
  const width =
    cell.width * (durationInDays + (!isMilestone ? 1 : 0)) || cell.width;
  const htmlElement = document.getElementById(`projectElement:${element.uuid}`);
  const htmlElementRoot = document.getElementById(
    `projectElementRoot:${element.uuid}`,
  );
  const top = htmlElement ? parseInt(getComputedStyle(htmlElement).top) : 10000;
  const left = htmlElementRoot
    ? parseInt(getComputedStyle(htmlElementRoot).marginLeft)
    : ((startDate.toMillis() - minDate.toMillis() + DAY) / DAY - 1) *
        cell.width -
      offset;

  return {
    left,
    width,
    height: cell.height,
    top: top,
    right: left + width + offset * 2,
  };
}

function getStartStartPath(origin: Rect, target: Rect, cell: Config["cell"]) {
  const isOriginAfterTarget = origin.left > target.left;

  return isOriginAfterTarget
    ? [
        [origin.left, origin.top + 15 / 2],
        [target.left - 15, origin.top + 15 / 2],
        [target.left - 15, target.top + 15 / 2],
        [target.left, target.top + 15 / 2],
      ]
    : [
        [origin.left, origin.top + 15 / 2],
        [origin.left - 15, origin.top + 15 / 2],
        [origin.left - 15, target.top + 15 / 2],
        [target.left, target.top + 15 / 2],
      ];
}

function getEndEndPath(origin: Rect, target: Rect, cell: Config["cell"]) {
  const isOriginAfterTarget = origin.right > target.right;

  return isOriginAfterTarget
    ? [
        [origin.right, origin.top + 15 / 2],
        [origin.right + 15, origin.top + 15 / 2],
        [origin.right + 15, target.top + 15 / 2],
        [target.right, target.top + 15 / 2],
      ]
    : [
        [origin.right, origin.top + 15 / 2],
        [target.right + 15, origin.top + 15 / 2],
        [target.right + 15, target.top + 15 / 2],
        [target.right, target.top + 15 / 2],
      ];
}

function getStartEndPath(origin: Rect, target: Rect, cell: Config["cell"]) {
  const isOriginAfterTarget = origin.left > target.right;

  return isOriginAfterTarget
    ? [
        [origin.left, origin.top + 15 / 2],
        [origin.left - 15, origin.top + 15 / 2],
        [origin.left - 15, target.top + 15 / 2],
        [target.right + 15, target.top + 15 / 2],
        [target.right + 15, target.top + 15 / 2],
        [target.right, target.top + 15 / 2],
      ]
    : [
        [origin.left, origin.top + 15 / 2],
        [origin.left - 15, origin.top + 15 / 2],
        [
          origin.left - 15,
          origin.top + cell.height - (cell.height - cell.height * HEIGHT_RATIO),
        ],
        [
          target.right + 15,
          origin.top + cell.height - (cell.height - cell.height * HEIGHT_RATIO),
        ],
        [target.right + 15, target.top + 15 / 2],
        [target.right, target.top + 15 / 2],
      ];
}

function getEndStartPath(origin: Rect, target: Rect, cell: Config["cell"]) {
  const isOriginAfterTarget = origin.right >= target.left;
  const isOriginBelowTarget = origin.top > target.top;

  return isOriginAfterTarget
    ? isOriginBelowTarget
      ? [
          [origin.right, origin.top + 15 / 2],
          [origin.right + 15, origin.top + 15 / 2],
          [
            origin.right + 15,
            origin.top - (cell.height - cell.height * HEIGHT_RATIO),
          ],
          [
            target.left - 15,
            origin.top - (cell.height - cell.height * HEIGHT_RATIO),
          ],
          [target.left - 15, target.top + 15 / 2],
          [target.left, target.top + 15 / 2],
        ]
      : [
          [origin.right, origin.top + 15 / 2],
          [origin.right + 15, origin.top + 15 / 2],
          [
            origin.right + 15,
            origin.top +
              cell.height -
              (cell.height - cell.height * HEIGHT_RATIO),
          ],
          [
            target.left - 15,
            origin.top +
              cell.height -
              (cell.height - cell.height * HEIGHT_RATIO),
          ],
          [target.left - 15, target.top + 15 / 2],
          [target.left, target.top + 15 / 2],
        ]
    : [
        [origin.right, origin.top + 15 / 2],
        [target.left - 15, origin.top + 15 / 2],
        [target.left - 15, target.top + 15 / 2],
        [target.left, target.top + 15 / 2],
      ];
}

const relMap = {
  ["START_START"]: getStartStartPath,
  ["END_END"]: getEndEndPath,
  ["START_END"]: getStartEndPath,
  ["END_START"]: getEndStartPath,
};

function getPath(
  constraint: Constraint & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  },
  minDate: DateTime,
  config: Config,
  userTimezone: string,
  offset: number = 0,
) {
  const { cell } = config;
  const {
    originDateType,
    targetDateType,
    originEntityUuid,
    originStartDate,
    originEndDate,
    originEntityType,
    targetEntityUuid,
    targetStartDate,
    targetEndDate,
    targetEntityType,
  } = constraint;
  const originRect = getElementRect(
    constraintToProjectElement(constraint, "origin"),
    minDate,
    cell,
    userTimezone,
    offset,
  );
  const targetRect = getElementRect(
    constraintToProjectElement(constraint, "target"),
    minDate,
    cell,
    userTimezone,
    offset,
  );
  const getRelPath =
    relMap[`${originDateType}_${targetDateType}` as keyof typeof relMap];

  return !!getRelPath ? getRelPath(originRect, targetRect, cell) : [];
}

function getDirection(path: number[][]): "left" | "right" | "" {
  const secondLastPoint = path[path.length - 2];
  const lastPoint = path[path.length - 1];

  return secondLastPoint && lastPoint
    ? secondLastPoint[0] < lastPoint[0]
      ? "right"
      : "left"
    : "";
}

function generateArrow(path: number[][], direction: "left" | "right" | "") {
  const lastPoint = path[path.length - 1];

  return direction === "right"
    ? `M ${lastPoint[0] - 4} ${lastPoint[1]} L ${lastPoint[0] - 4} ${
        lastPoint[1] + 3
      } L ${lastPoint[0] - 1} ${lastPoint[1]} L ${lastPoint[0] - 4} ${
        lastPoint[1] - 3
      } Z`
    : `M ${lastPoint[0] + 1} ${lastPoint[1]} L ${lastPoint[0] + 4} ${
        lastPoint[1] - 3
      } L ${lastPoint[0] + 4} ${lastPoint[1] + 3} Z`;
}

export {
  Context,
  useConstraints,
  useDrag,
  getPath,
  getDirection,
  generateArrow,
  constraintToProjectElement,
  constraintToEl,
  projectElementToList,
  projectElementToTask,
  projectElementToEvent,
  projectElementToMilestone,
};
